/**
 * wallShell — when does the app shell step aside for a FULL-SCREEN Practice Ops view?
 *
 * WHY THIS EXISTS (2026-10-30, plan §6 "Wall display hides the shell"; the Operatory joined it the same day, Nilay). Two views are a
 * screen of their own, not a page of the app: the Wall (a TV) and the Operatory (an iPad pinned to a room). They draw no sidebar, no top
 * bar, no tab bar, no welcome banners, no floating Agent button — nothing but the board and its own small header. Layout.vue draws the
 * shell and App.vue draws the Agent button, so BOTH ask this one question and cannot disagree:
 *
 *     isFullScreenView({ routeName, query, usesNewPage })
 *
 * true ONLY when ALL of these hold:
 *   1. the route is `PracticePulse` (the route's name never changed — Routes.js);
 *   2. the query says `view=wall` or `view=operatory`;
 *   3. the page that route renders is the NEW Practice Ops page. The classic Practice Pulse page ignores `?view=` and has neither view,
 *      so with the classic page such a link keeps its shell (otherwise it would show the classic board with no navigation).
 *
 * The decision reads the URL ONLY (never a stored preference), so a view chosen in the page's picker is reached by NAVIGATING to
 * `?view=<id>` and the shell and the page cannot disagree. Board and Phone are not full-screen: picking one navigates to `?view=board`
 * / `?view=phone`, which brings the shell back.
 *
 * `?view=` is read the way viewPref.js resolveView reads it: first value of a repeated param, trimmed, case-insensitive — so
 * `?view=Wall` is a Wall for the page and for the shell alike. `usesNewPage` comes from newPage.js (the SAME decision the route
 * component makes), never from here: this file stays import-free so the node-test executes it. (The file keeps the name it had when
 * the Wall was the only full-screen view.)
 *
 * ⚠️ Keep this file import-free. The node-tests execute this exact file.
 */

export const FULL_SCREEN_ROUTE_NAME = 'PracticePulse';
/** The views that hide the shell, as `?view=` spells them. */
export const FULL_SCREEN_VIEWS = Object.freeze(['wall', 'operatory']);

function norm(value) {
  return typeof value === 'string' ? value.trim().toLowerCase() : '';
}

/**
 * The full-screen view a route query asks for — 'wall' | 'operatory' — or '' for anything else
 * (`{ view: 'wall' }`, first of an array, case-insensitive).
 */
export function fullScreenViewOf(query) {
  if (!query || typeof query !== 'object') return '';
  const raw = Array.isArray(query.view) ? query.view[0] : query.view;
  const view = norm(raw);
  return FULL_SCREEN_VIEWS.indexOf(view) !== -1 ? view : '';
}

/**
 * @param {{routeName?: string, query?: object, usesNewPage?: boolean}} input
 * @returns {boolean} true ⇒ hide the shell (and every fixed overlay the shell's owners draw)
 */
export function isFullScreenView(input) {
  const { routeName, query, usesNewPage } = input && typeof input === 'object' ? input : {};
  return routeName === FULL_SCREEN_ROUTE_NAME && usesNewPage === true && fullScreenViewOf(query) !== '';
}
